import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { fromEvent, Subscription } from 'rxjs';

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit,
  Output, QueryList, Renderer2, SimpleChanges, ViewChild, ViewChildren, ViewEncapsulation
} from '@angular/core';

import type { IFilter, IFormFilter, IOrder } from './filters.interface';

import {
  BrandThemesEnum,
  DeviceService,
  GetElementUtils,
  IconSizeEnum,
  IHeading,
  IRangeOptions,
  ISdFormComponentItem,
  PositionEnum, SdAccordionComponent, SdAccordionGroupComponent,
  SdButtonComponent,
  SdButtonIconComponent,
  SdFormAutocompleteComponent,
  SdFormCheckboxGroupComponent,
  SdFormRadioGroupComponent,
  SdFormRangeComponent, SdFormSelectComponent, SdFormSelectMultipleComponent,
  SdIsCollapsibleDirective, SdTagComponent,
  SdThemeComponent, SdTitleComponent,
  SizesEnum,
  slideInOutHorizontallyAnimation,
  ThemesEnum,
  ThemesTextEnum
} from '@seb/web-design-system/core';

@Component({
  selector: 'sd-filters',
  templateUrl: './filters.component.html',
  animations: [
    slideInOutHorizontallyAnimation({
      anchor: 'slideInOutHorizontally',
      durationIn: 250,
      delayIn: 0,
      durationOut: 250,
      delayOut: 0,
      translate: '150%'
    })
  ],
  styleUrls: ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { ngSkipHydration: 'true' },
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SdIsCollapsibleDirective,
    SdFormSelectComponent,
    SdTitleComponent,
    NgTemplateOutlet,
    SdButtonComponent,
    SdFormRangeComponent,
    SdFormSelectMultipleComponent,
    SdTagComponent,
    SdButtonIconComponent,
    SdAccordionGroupComponent,
    SdAccordionComponent,
    SdFormAutocompleteComponent,
    SdFormCheckboxGroupComponent,
    SdFormRadioGroupComponent,
    TranslateModule
  ]
})
export class SdFiltersComponent extends SdThemeComponent implements OnChanges, OnDestroy, OnInit {
  @Input()
  orderSelectConfig: IOrder;

  @Input()
  get formRange() {
    return this._formRange;
  }
  set formRange(formRange: IRangeOptions) {
    this._formRange = formRange;
    this.formGroup?.patchValue({
      minSelected: this.formRange?.minValue,
      maxSelected: this.formRange?.maxValue
    });
  }

  @Input()
  heading: IHeading;

  @Input()
  numberOfResults: number;

  @Input()
  set filters(filters: IFilter[]) {
    this._filters = filters?.map((it) => ({ ...it }));
  }

  @Input()
  superFilters: { value: boolean; label: string }[];

  @Input()
  titleLevel: number;

  @Input()
  idFilterCollapse = 'filterCollapseId';

  @Input()
  hasAutocomplete = false;

  @Input()
  showButton = true;

  @Input()
  isAlwaysOpen = false;

  @ViewChild('action', { read: ElementRef })
  triggerAction: ElementRef;

  @ViewChild('hide', { read: ElementRef })
  panel: ElementRef;

  @ViewChild('closeModalButton', { read: ElementRef })
  closeModalButton: ElementRef;

  @ViewChild('mobileFilterButton', { read: ElementRef })
  mobileFilterButton: ElementRef;

  @ViewChild('filtersAccordion')
  filtersAccordion: SdAccordionGroupComponent;

  @ViewChild('inputRef', { read: ElementRef })
  inputRef: ElementRef;

  @ViewChildren('selectMultiple')
  selectMultiples: QueryList<SdFormSelectMultipleComponent>;

  @Output()
  selectedFilters = new EventEmitter<any>();

  @Output()
  selectedSuperFilters = new EventEmitter<any>();

  @Output()
  orderedBy = new EventEmitter<string>();

  @Output()
  selectedPriceRange = new EventEmitter<{ min: number; max: number }>();

  subscriptions = new Subscription();
  positionEnum = PositionEnum;
  themesEnum = ThemesEnum;
  themesTextEnum = ThemesTextEnum;
  iconSize = IconSizeEnum;
  getElementUtils = GetElementUtils;
  accordionOpen = false;
  isOpenFilters = false;
  formGroup!: FormGroup;
  _filters: IFilter[] = [];
  isMobile$: boolean;
  isDevice$: boolean;
  displayPrice: boolean;
  priceMin: number;
  priceMax: number;
  filterTags: IFormFilter[];
  bodyChildren;
  sizeEnum = SizesEnum;
  isSelectScroll = false;
  _formRange: IRangeOptions;
  brandThemesEnum = BrandThemesEnum;
  scrollSubscription: Subscription;

  get minSelected() {
    return this.formGroup?.get('minSelected') as FormControl;
  }

  get maxSelected() {
    return this.formGroup?.get('maxSelected') as FormControl;
  }

  get items() {
    return this.formGroup.get('items') as FormArray<FormControl<IFormFilter>>;
  }

  constructor(
    private fb: FormBuilder,
    private renderer: Renderer2,
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.bodyChildren = this.document.body.children;
    }

    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile$ = x;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.deviceService.isDevice.subscribe((x) => {
        this.isDevice$ = x;

        if (isPlatformBrowser(this.platformId)) {
          if (this.isDevice$) {
            this.scrollSubscription?.unsubscribe();
          } else {
            this.scrollSubscription = fromEvent(window, 'scroll').subscribe(() => {
              if (this.isOpenFilters && !this.isAlwaysOpen) {
                const scrollOffset =
                  window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

                if (scrollOffset > window.pageYOffset + this.elRef.nativeElement.getBoundingClientRect().top) {
                  this.closeFilters();
                  this.cdRef.markForCheck();
                }
              }
            });
          }
        }

        this.cdRef.markForCheck();
      })
    );

    if (this.isAlwaysOpen && !this.isDevice$) {
      this.isOpenFilters = true;
    }

    this.updateTag();
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    this.formGroup = this.fb.group({
      orderBy: {},
      items: this.initForm(this._filters),
      minSelected: this.minSelected ?? this.formRange?.minValue,
      maxSelected: this.maxSelected ?? this.formRange?.maxValue
    });

    if (this.minSelected.value && this.maxSelected.value) {
      this.priceMin = this.minSelected.value;
      this.priceMax = this.maxSelected.value;
    } else {
      this.priceMin = this.formRange?.minValue;
      this.priceMax = this.formRange?.maxValue;
    }

    this.displayPrice = !(this.priceMin === this.formRange?.minValue && this.priceMax === this.formRange?.maxValue);

    this.updateTag();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  priceRangeChange() {
    this.priceMin = this.minSelected.value;
    this.priceMax = this.maxSelected.value;
    this.displayPrice = !(this.priceMin === this.formRange?.minValue && this.priceMax === this.formRange?.maxValue);
    this.selectedPriceRange?.emit({
      max: this.maxSelected.value,
      min: this.minSelected.value
    });
    this.updateTag();
  }

  removeTag(tag: 'price' | IFormFilter): void {
    /**
     * Manage Price
     */
    if (tag === 'price') {
      this.minSelected.setValue(this.formRange?.minValue);
      this.maxSelected.setValue(this.formRange?.maxValue);
      this.displayPrice = false;
      this.selectedFilters.emit({
        value: 'PRICE_RANGE',
        libelle: 'PRICE_RANGE'
      });
    } else {
      /**
       * Manage Filters
       */
      tag.filterValue.checked = false;

      this.items.value.forEach((filter, index) => {
        if ((filter.id === tag.id) && (filter.filterValue.value === tag.filterValue.value)) {
          this.items.at(index).setValue(tag);
        }
      });

      this.selectFilter(tag.filterValue);
    }
  }

  updateTag(): void {
    this.filterTags = this.items.value.filter(
      (checked) => checked.filterValue.checked === true,
    );
  }

  selectFilter(itemChanged: ISdFormComponentItem): void {
    this.selectedFilters.emit(itemChanged);
    if (itemChanged?.value && this.selectMultiples) {
      for (const selectMultiple of this.selectMultiples) {
        selectMultiple.updatePosition();
      }
      this.cdRef.markForCheck();
    }
  }

  onClickOut(event: boolean) {
    this.accordionOpen = event;
    const body = this.getElementUtils.byId('appWrapper', this.document.body);
    if (
      !this.isOpenFilters &&
      this.isDevice$ &&
      body.classList.contains('has-no-overflow')
    ) {
      this.renderer.removeClass(body, 'has-no-overflow');
    }
  }

  toggleFilters() {
    this.isOpenFilters = !this.isOpenFilters;
    /* A11y */
    setTimeout(() => {
      if (this.isOpenFilters) {
        this.closeModalButton?.nativeElement.focus();
        for (const child of this.bodyChildren) {
          if (child.className !== 'cdk-overlay-container') {
            child.setAttribute('aria-hidden', true);
          }
        }

        if (this.isDevice$) {
          const body = this.getElementUtils.byId('appWrapper', this.document.body);
          this.renderer.addClass(body, 'has-no-overflow');
        }
      } else {
        this.mobileFilterButton?.nativeElement.focus();
        for (const child of this.bodyChildren) {
          child.removeAttribute('aria-hidden');
        }
      }
    });
  }

  selectOrder(): void {
    if (this.formGroup && this.orderSelectConfig) {
      this.orderedBy.emit(
        this.formGroup.get('orderBy').value[this.orderSelectConfig.valueKey],
      );
    }
  }

  closeFilters(): void {
    if (!this.isSelectScroll) {
      this.closeSelectsMultiple();
      this.isOpenFilters = false;
    }
  }

  initForm(filters: IFilter[]): FormArray<FormControl<IFormFilter>> {
    const filterArr = new FormArray<FormControl<IFormFilter>>([]);

    filters.forEach((filterCat: IFilter, index) => {
      filterCat.values.forEach((filterValue: ISdFormComponentItem) => {
        filterArr.push(
          new FormControl({
            cat: filterCat.name,
            id: index,
            filterValue
          })
        );
      });
    });

    return filterArr;
  }

  clearFilters() {
    /**
     * Manage price
     */
    this.minSelected.setValue(this.formRange?.minValue);
    this.maxSelected.setValue(this.formRange?.maxValue);
    this.displayPrice = false;

    this.selectedFilters.emit(null);
  }

  onOpenFilters() {
    const body = this.getElementUtils.byId('appWrapper', this.document.body);
    if (this._filters.length && this.panel?.nativeElement) {
      /* A11y */
      if (this.isOpenFilters) {
        this.renderer.setAttribute(this.panel?.nativeElement, 'tabindex', '-1');
      } else {
        this.renderer.removeAttribute(this.panel?.nativeElement, 'tabindex');
        if (this.isDevice$ && body.classList.contains('has-no-overflow')) {
          this.renderer.removeClass(body, 'has-no-overflow');
        }
      }
    }
  }

  toggleSuperFilter(event): void {
    const currentFilter = this.superFilters.find((filter) => filter === event);
    currentFilter.value = !event.value;
    this.selectedSuperFilters.emit(currentFilter);
  }

  closeSelectsMultiple(): void {
    for (const selectMultiple of this.selectMultiples) {
      selectMultiple.close();
    }
  }

  getSelectFilterSelectedValue(selectFilter: IFilter): ISdFormComponentItem | undefined {
    return selectFilter.values?.find((filterValue: ISdFormComponentItem) => filterValue.checked);
  }

  onChangeAutocomplete(event): void {
    this.selectedFilters.emit(event);
  }
}
